import { Suspense } from "react";
import { createHashRouter } from "react-router-dom";
import type { FallbackProps } from "react-error-boundary";
import MobileLayout from "../../layouts/Mobile";
import { routes } from "./routes";
import { routes as publicRoutes } from "../public/routes";
import { PageSizeLoader } from "../../components/CustomComponents/PageSizeLoader";
import { AppErrorBoundary } from "../../components/CustomComponents/PageErrorBoundry";
import { ErrorComponent } from "../../components/CustomComponents/PageErrorBoundry/ErrorComponent";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useAppSelector } from "../../hooks/redux";
import { HomeHealthRouteGuard } from "./HomeHealthRouteGuard";
import { SuperadminRouteGuard } from "./SuperadminRouteGuard";
import { EligibilityRouteGuard } from "./EligibilityRouteGuard";
import { BetaRouteGuard, type BetaGatedFeature } from "./BetaRouteGuard";

/** Beta-only routes and the entitlement gating each one. The gate is applied
 *  at render time by ``BetaRouteGuard`` (NOT by dropping the route from the
 *  tree) so the router instance never has to be rebuilt when entitlements
 *  resolve — rebuilding it remounts every mounted page. */
const betaGatedFeatureForRoute: Record<string, BetaGatedFeature | undefined> = {
  neo: "neo",
  neoWorkflows: "neo",
  telehealth: "telehealth",
  telehealthCall: "telehealth",
};

/** Reads connectivity from redux at render time so the error fallback stays
 *  current without baking ``connectionStatus`` into the router instance. */
const RouteErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  const connectionStatus = useAppSelector(
    (state) => state.network.connectionStatus
  );
  return (
    <ErrorComponent
      error={error}
      resetErrorBoundary={resetErrorBoundary}
      connectionStatus={connectionStatus}
    />
  );
};

export const mobileRouter = () => {
  const TelehealthJoinElement = publicRoutes.telehealthJoin.element;

  return createHashRouter([
    {
      path: publicRoutes.telehealthJoin.path,
      element: (
        <Suspense
          fallback={
            <PageSizeLoader
              loader={
                <Spin
                  indicator={
                    <LoadingOutlined
                      style={{ fontSize: 48, color: "#161646" }}
                      spin
                    />
                  }
                />
              }
            />
          }
        >
          <TelehealthJoinElement />
        </Suspense>
      ),
    },
    {
      path: "/",
      element: <MobileLayout />,
      children: Object.entries(routes).map(([key, route]) => {
        const gatedFeature = betaGatedFeatureForRoute[key];
        const isRedirectRoute = ["", "*"].includes(route.path);
        const routeElement = (
          <SuperadminRouteGuard>
            <EligibilityRouteGuard>
              {gatedFeature ? (
                <BetaRouteGuard feature={gatedFeature}>
                  <route.element />
                </BetaRouteGuard>
              ) : (
                <route.element />
              )}
            </EligibilityRouteGuard>
          </SuperadminRouteGuard>
        );

        return {
          ...route,
          element: (
            <AppErrorBoundary
              FallbackComponent={RouteErrorFallback}
              resetKeys={[route.path]}
            >
              <Suspense
                fallback={
                  <PageSizeLoader
                    loader={
                      <Spin
                        indicator={
                          <LoadingOutlined
                            style={{ fontSize: 48, color: "#161646" }}
                            spin
                          />
                        }
                      />
                    }
                  />
                }
              >
                {isRedirectRoute ? (
                  routeElement
                ) : (
                  <HomeHealthRouteGuard>{routeElement}</HomeHealthRouteGuard>
                )}
              </Suspense>
            </AppErrorBoundary>
          ),
        };
      }),
    },
  ]);
};
